import { useCallback, useMemo, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { TimePicker } from '@material-ui/pickers';
import clsx from 'clsx';
import { useAsyncEffect, useAsyncState } from '@hilma/tools';

import { formatTime, getDateTime } from '../functions';
import { useForm, useLanguage, useGi18n, useTranslateAndReplace } from '../hooks';

import { useStyles as useFormFileStyles } from './FormFile';
import FormInput, { FormInputProps } from './FormInput';

const FormTimeRange: React.FC<FormInputProps> = ({ id, disabled, classes: formInputClasses = {}, ...rest }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [currentTime, setCurrentTime, getCurrentTime] = useAsyncState<'first' | 'second'>('first');
	const [firstTime, setFirstTime, getFirstTime] = useAsyncState<Date | null>(null);
	const [secondTime, setSecondTime, getSecondTime] = useAsyncState<Date | null>(null);
	const [error, setError] = useState('');

	const classes = useStyles();
	const formFileClasses = useFormFileStyles();

	const ctr = useGi18n()(i18n => i18n.error?.form?.chooseTimeRange);

	const form = useForm();
	const language = useLanguage();
	const translateAndReplace = useTranslateAndReplace();

	const range = useMemo(() => form.values[id!] as [Date, Date] | null, [form.values[id!]]);

	const value = useMemo(() => {
		if (!range) return ctr;
		return `${formatTime(range[0], language)} - ${formatTime(range[1], language)}`;
	}, [range, language, ctr]);

	const onOpen = useCallback(() => {
		setFirstTime(range ? range[0] : null);
		setSecondTime(range ? range[1] : null);
		setIsOpen(true);
	}, [range]);

	const onCancel = useCallback(() => {
		setIsOpen(false);
	}, []);

	const generateError = useCallback(async (): Promise<string> => {

		if (!await getFirstTime() || !await getSecondTime()) return '';
		if (getDateTime((await getFirstTime())!) < getDateTime((await getSecondTime())!)) return '';

		if (await getCurrentTime() === 'first') return translateAndReplace({ path: 'error.timeBefore', values: [await getSecondTime()!.then(date => date!.toLocaleTimeString())] });
		else if (await getCurrentTime() === 'second') return translateAndReplace({ path: 'error.timeAfter', values: [await getSecondTime()!.then(date => date!.toLocaleTimeString())] });

		return '';
	}, [language]);

	const onOk = useCallback(async () => {
		if (await getCurrentTime() === 'first' && !await getFirstTime()) await setFirstTime(new Date());
		if (await getCurrentTime() === 'second' && !await getSecondTime()) await setSecondTime(await getFirstTime() ?? new Date());

		if (await getCurrentTime() === 'first' && !await getSecondTime()) return setCurrentTime('second');
		if (await getCurrentTime() === 'second' && !await getFirstTime()) return setCurrentTime('first');

		if (await generateError()) return;

		form.setFieldValue(id, [await getFirstTime(), await getSecondTime() ?? await getFirstTime()]);
		setIsOpen(false);
	}, [form, error, id]);

	const onExited = useCallback(() => {
		setFirstTime(null);
		setSecondTime(null);
		setCurrentTime('first');
	}, []);

	useAsyncEffect(async () => {
		setError(await generateError());
	}, [firstTime, secondTime, currentTime]);

	return (
		<>
			<FormInput
				id={id}
				endAdornment={(
					<IconButton
						color="primary"
						className={formFileClasses.iconButton}
					>
						<AccessTimeIcon className={formFileClasses.svg} />
					</IconButton>
				)}
				readOnly
				{...rest}
				classes={{
					...formInputClasses,
					input: clsx(formInputClasses.input, value !== ctr && classes.input)
				}}
				onClick={disabled ? undefined : onOpen}
				disabled={disabled}
				value={value}
			/>

			<Dialog open={isOpen && !disabled} onExited={onExited}>
				<DialogTitle className={classes.dialogTitle}>
					<Button
						onClick={() => setCurrentTime('first')}
						classes={{
							root: classes.timeButton,
							label: clsx(classes.timeButtonLabel, currentTime === 'first' && classes.currentLabel)
						}}
					>
						{firstTime ? formatTime(firstTime, language) : '00:00'}
					</Button>
					<span className={classes.timeButtonLabel}> - </span>
					<Button
						onClick={() => setCurrentTime('second')}
						classes={{
							root: classes.timeButton,
							label: clsx(classes.timeButtonLabel, currentTime === 'second' && classes.currentLabel)
						}}
					>
						{secondTime ? formatTime(secondTime, language) : '00:00'}
					</Button>
				</DialogTitle>
				<TimePicker
					key={currentTime}
					ampm={false}
					value={currentTime === 'first' ? firstTime : secondTime}
					onChange={currentTime === 'first' ? setFirstTime : setSecondTime}
					variant="static"
					TextFieldComponent={() => null}
				/>
				<DialogActions className={classes.error}>{error}</DialogActions>
				<DialogActions>
					<Button color="primary" onClick={onCancel}>cancel</Button>
					<Button color="primary" className={classes.okButton} onClick={onOk}>ok</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default FormTimeRange;

const useStyles = makeStyles(theme => ({
	input: {
		direction: theme.direction
	},

	dialogTitle: {
		direction: theme.direction,
		background: theme.palette.primary.main,
		paddingBottom: 0,
	},

	timeButton: {
		padding: 0
	},

	timeButtonLabel: {
		color: 'rgba(255, 255, 255, 0.54)',
		fontSize: 20,
	},

	currentLabel: {
		color: 'white'
	},

	error: {
		color: theme.palette.error.main,
		justifyContent: 'flex-start'
	},

	okButton: {
		marginLeft: 8
	}
}));